/**
 * The acid layer.
 *
 * A single full-screen pass that warps, swirls, separates and hue-cycles the
 * rendered frame. Every drive value comes from IntoxicationSystem, i.e. from the
 * run's `simulationTime` and the dose - so the trip looks the same on every
 * attempt, and at 0 % the pass is skipped entirely and the frame is untouched.
 *
 * The HUD is DOM, not WebGL, so the timer stays readable no matter how hard the
 * world is melting. That is deliberate: this is still a race.
 */

import * as THREE from 'three';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import type { PsychedelicSample } from '../player/IntoxicationSystem';

const vertexShader = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform sampler2D tDiffuse;
  uniform float uTime;
  uniform float uIntensity;
  uniform float uWarp;
  uniform float uWarpFrequency;
  uniform float uWarpSpeed;
  uniform float uChroma;
  uniform float uHue;
  uniform float uHueRadial;
  uniform float uSaturation;
  uniform float uBreathe;
  uniform float uSwirl;
  uniform float uRipple;
  uniform float uRippleFrequency;
  varying vec2 vUv;

  vec3 hueRotate(vec3 color, float angle) {
    const vec3 k = vec3(0.57735);
    float c = cos(angle);
    return color * c + cross(k, color) * sin(angle) + k * dot(k, color) * (1.0 - c);
  }

  void main() {
    vec2 centered = vUv - 0.5;
    float radius = length(centered);

    // Breathing: the whole world inhales.
    vec2 uv = 0.5 + centered * (1.0 - uBreathe);

    // Swirl, strongest in the middle so the edges stay navigable.
    float angle = uSwirl * (1.0 - clamp(radius * 1.6, 0.0, 1.0));
    float s = sin(angle);
    float c = cos(angle);
    vec2 rotated = uv - 0.5;
    uv = 0.5 + vec2(rotated.x * c - rotated.y * s, rotated.x * s + rotated.y * c);

    // Domain warp, two octaves.
    float t = uTime * uWarpSpeed;
    uv.x += sin(uv.y * uWarpFrequency + t) * uWarp;
    uv.y += sin(uv.x * uWarpFrequency * 0.8 - t * 0.9) * uWarp;
    uv.x += sin(uv.y * uWarpFrequency * 2.6 + t * 1.9) * uWarp * 0.35;
    uv.y += cos(uv.x * uWarpFrequency * 2.2 + t * 1.5) * uWarp * 0.35;

    // Fine ripple crawling outwards from the centre.
    uv += normalize(centered + 1e-5) * sin(radius * uRippleFrequency - uTime * 2.4) * uRipple;

    uv = clamp(uv, 0.0015, 0.9985);

    // Chromatic separation along the radial direction.
    vec2 dir = normalize(centered + 1e-5) * uChroma;
    vec3 color;
    color.r = texture2D(tDiffuse, clamp(uv + dir, 0.0, 1.0)).r;
    color.g = texture2D(tDiffuse, uv).g;
    color.b = texture2D(tDiffuse, clamp(uv - dir, 0.0, 1.0)).b;

    const vec3 W = vec3(0.299, 0.587, 0.114);
    float sourceLuma = dot(color, W);

    // Hue rotation with a radial gradient, so colour crawls across the view.
    color = hueRotate(color, uHue + radius * uHueRadial);
    color = mix(vec3(dot(color, W)), color, 1.0 + uSaturation);

    // Rotating hue and pushing saturation both throw channels out of gamut, and
    // clamping them turns saturated surfaces into black holes. Rescaling back to
    // the source luminance keeps the trip colourful but still navigable - this
    // is a race, and you have to be able to see the doorway.
    float shiftedLuma = max(dot(color, W), 1e-4);
    color *= clamp(sourceLuma / shiftedLuma, 0.35, 2.5);

    gl_FragColor = vec4(clamp(color, 0.0, 1.0), 1.0);
  }
`;

export class PsychedelicPass extends ShaderPass {
  constructor() {
    super({
      name: 'PsychedelicShader',
      uniforms: {
        tDiffuse: { value: null },
        uTime: { value: 0 },
        uIntensity: { value: 0 },
        uWarp: { value: 0 },
        uWarpFrequency: { value: 9 },
        uWarpSpeed: { value: 1.7 },
        uChroma: { value: 0 },
        uHue: { value: 0 },
        uHueRadial: { value: 0 },
        uSaturation: { value: 0 },
        uBreathe: { value: 0 },
        uSwirl: { value: 0 },
        uRipple: { value: 0 },
        uRippleFrequency: { value: 23 },
      },
      vertexShader,
      fragmentShader,
    } satisfies THREE.ShaderMaterialParameters & { name: string });
  }

  /** Feeds one frame's worth of drive values. `time` is the run's sim time. */
  apply(sample: PsychedelicSample, time: number, frequency: number, speed: number, rippleFrequency: number): void {
    const u = this.uniforms;
    u.uTime.value = time;
    u.uIntensity.value = sample.intensity;
    u.uWarp.value = sample.warp;
    u.uWarpFrequency.value = frequency;
    u.uWarpSpeed.value = speed;
    u.uChroma.value = sample.chroma;
    u.uHue.value = sample.hue;
    u.uHueRadial.value = sample.hueRadial;
    u.uSaturation.value = sample.saturation;
    u.uBreathe.value = sample.breathe;
    u.uSwirl.value = sample.swirl;
    u.uRipple.value = sample.ripple;
    u.uRippleFrequency.value = rippleFrequency;
  }
}
